/**
 * TeamSystem Impact - Year Lifecycle Panel
 *
 * Gestione del ciclo di vita degli anni di rendicontazione, in due livelli:
 *  1. Archivia (reversibile): l'anno sparisce dai menu ma resta nel database.
 *  2. Elimina definitivamente (irreversibile): consentito SOLO su un anno
 *     già archiviato; mostra prima il riepilogo di cosa verrà eliminato e
 *     chiede di ridigitare l'anno per conferma.
 *
 * L'anno di lavoro attualmente selezionato non può essere archiviato.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  DeleteForever as DeleteForeverIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useWorkingYear } from '../../../store/workingYearStore';
import { useAuthStore } from '../../../store/authStore';
import { isReadOnlyRole } from '../../../utils/permissions';
import {
  reportingYearsApi,
  type ManagedYear,
  type DeletionPreview,
} from '../../../api/reportingYears.api';

export function YearLifecyclePanel() {
  const { t, i18n } = useTranslation();
  const k = (key: string, opts?: any): string => t(`dma.dmaSettings.yearLifecycle.${key}`, opts) as string;
  const tx = (key: string): string => t(key) as string;
  const { year: workingYear, setAvailableYears } = useWorkingYear();
  const utente = useAuthStore((s) => s.utente);
  const isReadOnly = isReadOnlyRole(utente?.ruolo);

  const [years, setYears] = useState<ManagedYear[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyYear, setBusyYear] = useState<number | null>(null);

  // Archive confirm dialog
  const [archiveTarget, setArchiveTarget] = useState<number | null>(null);

  // Delete dialog (2 fasi: anteprima → conferma digitando l'anno)
  const [deleteYear, setDeleteYear] = useState<number | null>(null);
  const [deletePhase, setDeletePhase] = useState<'preview' | 'confirm'>('preview');
  const [preview, setPreview] = useState<DeletionPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadYears = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportingYearsApi.listManaged();
      setYears(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadYears();
  }, [loadYears]);

  /** Riallinea il selettore globale dopo una modifica. */
  const refreshGlobalYears = async () => {
    try {
      const active = await reportingYearsApi.list();
      setAvailableYears(active);
    } catch {
      // non bloccante
    }
  };

  const handleArchive = async (year: number) => {
    try {
      setBusyYear(year);
      setError(null);
      await reportingYearsApi.archive(year);
      setArchiveTarget(null);
      setSuccess(k('archived', { year }));
      await loadYears();
      await refreshGlobalYears();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyYear(null);
    }
  };

  const handleRestore = async (year: number) => {
    try {
      setBusyYear(year);
      setError(null);
      await reportingYearsApi.restore(year);
      setSuccess(k('restored', { year }));
      await loadYears();
      await refreshGlobalYears();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusyYear(null);
    }
  };

  const openDeleteDialog = async (year: number) => {
    setDeleteYear(year);
    setDeletePhase('preview');
    setConfirmText('');
    setPreview(null);
    setPreviewLoading(true);
    try {
      const data = await reportingYearsApi.deletionPreview(year);
      setPreview(data);
    } catch (err: any) {
      setError(err.message);
      setDeleteYear(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteYear(null);
    setPreview(null);
    setConfirmText('');
    setDeletePhase('preview');
  };

  const handleConfirmDelete = async () => {
    if (deleteYear === null || confirmText !== String(deleteYear)) return;
    try {
      setDeleting(true);
      setError(null);
      await reportingYearsApi.remove(deleteYear);
      setSuccess(k('deleted', { year: deleteYear }));
      closeDeleteDialog();
      await loadYears();
      await refreshGlobalYears();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const nonEmptyBreakdown = preview?.breakdown.filter((b) => b.count > 0) ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ArchiveIcon color="action" />
        <Typography variant="h6">{k('title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {k('description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : years.length === 0 ? (
        <Alert severity="info">{k('noYears')}</Alert>
      ) : (
        <Table size="small">
          <TableBody>
            {years.map((y) => {
              const isWorkingYear = y.year === workingYear;
              return (
                <TableRow key={y.year} hover>
                  <TableCell sx={{ width: 90 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {y.year}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 140 }}>
                    <Chip
                      size="small"
                      label={y.archived ? k('statusArchived') : k('statusActive')}
                      color={y.archived ? 'default' : 'success'}
                      variant={y.archived ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {isReadOnly ? null : y.archived ? (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          startIcon={<UnarchiveIcon />}
                          onClick={() => handleRestore(y.year)}
                          disabled={busyYear === y.year}
                        >
                          {k('restore')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteForeverIcon />}
                          onClick={() => openDeleteDialog(y.year)}
                          disabled={busyYear === y.year}
                        >
                          {k('deletePermanently')}
                        </Button>
                      </Box>
                    ) : (
                      <Tooltip title={isWorkingYear ? k('cannotArchiveActive') : ''}>
                        <span>
                          <Button
                            size="small"
                            startIcon={<ArchiveIcon />}
                            onClick={() => setArchiveTarget(y.year)}
                            disabled={isWorkingYear || busyYear === y.year}
                          >
                            {k('archive')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Archive confirm dialog */}
      <Dialog open={archiveTarget !== null} onClose={() => setArchiveTarget(null)}>
        <DialogTitle>{k('archiveConfirmTitle', { year: archiveTarget })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{k('archiveConfirmText')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>{tx('dma.dmaSettings.cancel')}</Button>
          <Button
            variant="contained"
            startIcon={<ArchiveIcon />}
            onClick={() => archiveTarget !== null && handleArchive(archiveTarget)}
            disabled={busyYear !== null}
          >
            {k('archive')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog (2 fasi) */}
      <Dialog open={deleteYear !== null} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          {k('deleteTitle', { year: deleteYear })}
        </DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : deletePhase === 'preview' ? (
            <>
              {nonEmptyBreakdown.length === 0 ? (
                <DialogContentText>{k('deletePreviewEmpty')}</DialogContentText>
              ) : (
                <>
                  <DialogContentText sx={{ mb: 1 }}>{k('deletePreviewIntro')}</DialogContentText>
                  <List dense disablePadding>
                    {nonEmptyBreakdown.map((b) => (
                      <ListItem key={b.key} disableGutters sx={{ py: 0 }}>
                        <ListItemText
                          primary={`${b.count.toLocaleString(i18n.language)} ${k(`entities.${b.key}`, { defaultValue: b.label })}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">
                    {k('deletePreviewTotal')}: {(preview?.total ?? 0).toLocaleString(i18n.language)}
                  </Typography>
                </>
              )}
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
                {k('deleteWarningIrreversible')}
              </Alert>
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {k('deleteConfirmInstruction', { year: deleteYear })}
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={k('typeYearPlaceholder', { year: deleteYear })}
                error={confirmText.length > 0 && confirmText !== String(deleteYear)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>{tx('dma.dmaSettings.cancel')}</Button>
          {!previewLoading && deletePhase === 'preview' && (
            <Button color="error" variant="contained" onClick={() => setDeletePhase('confirm')}>
              {k('continue')}
            </Button>
          )}
          {deletePhase === 'confirm' && (
            <Button
              color="error"
              variant="contained"
              startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
              onClick={handleConfirmDelete}
              disabled={deleting || confirmText !== String(deleteYear)}
            >
              {k('deletePermanently')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
